import {
  CashPaymentMethod,
  CheckPaymentMethod,
  CreditPaymentMethod,
  CustomPaymentMethod,
  OtherPaymentMethod,
  Payment,
  PaymentMethodType,
  StatementPaymentMethod,
  StripeCard,
  StripePaymentMethodRequest,
} from '../../clients/quipli-service/payment/payment';
import { Radio } from '../../components/radio/radio';
import { ReactNode, useEffect, useState } from 'react';
import './payment-method-selector.scss';
import { Field } from '../../components/field/field';
import { Spinner } from '../../components/spinner/spinner';
import Icon from '../../components/icons/icon';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { Button } from '../../components/form/button';
import { usePaymentApiV1 } from '../../clients/quipli-service/payment/payment-api-v1';
import { PaymentMethodRequestV2, StripeBankAccount } from '../../clients/quipli-service/payment/payment-v2';
import { TerminalSelect } from './stripe/terminal-component/terminal-select';
import { Typography } from '../../components/typography/typography';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { usePaymentApiV2 } from '../../clients/quipli-service/payment/payment-api-v2';
import { CardPaymentMethodInput } from './card-payment-method-input';
import { User } from '../../clients/quipli-service/user/user';
import { useInvoiceApiV1 } from '../../clients/quipli-service/invoice/invoice-api-v1';
import { displayMoneyFormat } from '../../util/util';
import { toast } from '../../util/toasts';
import { SelectForm } from '../../components/select-form/select-form';
import { components } from 'react-select';
import { Location } from '../../clients/quipli-service/location/location';
import { usePaymentConfigApiV1 } from '../../clients/quipli-service/payment-config/payment-config-api-v1';
import { usePayment } from '../../hooks/use-payment-context';
import { PaymentMethodConfigType } from '../../clients/quipli-service/payment-config/payment-config';

export interface PaymentMethodSelectorProps {
  user: User;
  allowedPaymentMethods?: PaymentMethodType[];
  paymentMethod?: PaymentMethodRequestV2;
  setPaymentMethod: (pm: PaymentMethodRequestV2) => void;
  setPaymentMethodType?: (pmt: PaymentMethodType) => void;
  locationId: number;
  onComplete?: (payments?: Payment[]) => void;
  onCancel?: () => void;
  card?: StripeCard;
  setCard?: (card: StripeCard) => void;
  payments?: Payment[];
  setPayments?: (payments: Payment[]) => void;
  facilitate?: boolean;
  balance?: number;
  setAmount: (amount: number) => void;
}
export const PaymentMethodSelectorV2 = ({
  user,
  paymentMethod,
  setPaymentMethod,
  setPaymentMethodType,
  allowedPaymentMethods = Object.values(PaymentMethodType),
  locationId,
  setCard,
  payments,
  setPayments,
  facilitate = false,
  onComplete,
  balance,
  setAmount,
}: PaymentMethodSelectorProps) => {
  const { listTerminals } = usePaymentApiV1();
  const { getMultipleConfigurations } = useAPI();
  const { getStripeAccount } = usePaymentConfigApiV1();

  const { getLocationById } = useAPI();

  const paymentContext = usePayment();

  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<PaymentMethodType>(
    paymentMethod?.type || PaymentMethodType.Stripe,
  );

  const [terminalOnline, setTerminalOnline] = useState<boolean>(false);
  const [stripeEnabled, setStripeEnabled] = useState<boolean>(true);
  const [stripePaymentTypeSelected, setStripePaymentTypeSelected] = useState<
    'card' | 'bankAccount' | 'terminal' | 'terminal-bank' | undefined
  >(
    paymentMethod?.type == PaymentMethodType.Stripe
      ? paymentMethod.method == 'ACH'
        ? 'bankAccount'
        : 'card'
      : undefined,
  );
  const [customType, setCustomType] = useState<string>();
  const [achEnabled, setAchEnabled] = useState<boolean>(false);
  const [statementPaymentsEnabled, setStatementPaymentsEnabled] = useState<boolean>(false);
  const [location, setLocation] = useState<Location>();

  async function init() {
    const stripeAccount = await getStripeAccount();
    if (stripeAccount) {
      const terminals = await listTerminals(locationId);
      const online = terminals.some((t) => {
        return t.status == 'online';
      });
      setTerminalOnline(online);
    } else {
      setStripeEnabled(false);
    }
    const config = await getMultipleConfigurations(['CONF_ENABLE_ACH', 'CONF_STATEMENT_PAYMENTS']);
    setAchEnabled(config.find((_) => _.variable == 'CONF_ENABLE_ACH')?.value === '1');
    setStatementPaymentsEnabled(config.find((_) => _.variable == 'CONF_STATEMENT_PAYMENTS')?.value === '1');
    const _location = await getLocationById(locationId);
    setLocation(_location);
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (setPaymentMethodType) {
      setPaymentMethodType(selectedPaymentMethodType);
    }
  }, [selectedPaymentMethodType]);

  useEffect(() => {
    if (paymentMethod) {
      setSelectedPaymentMethodType(paymentMethod.type);
      if (paymentMethod.type === PaymentMethodType.Stripe) {
        if (paymentMethod.cardId) {
          setStripePaymentTypeSelected('card');
        } else if (paymentMethod.bankAccountId) {
          setStripePaymentTypeSelected('bankAccount');
        }
      }
    }
  }, [paymentMethod]);

  return paymentContext ? (
    <div className="payment-method-selector">
      <h6 className="prompt p-2">Choose payment method</h6>
      <div className="payment-method-radio">
        {allowedPaymentMethods.includes(PaymentMethodType.Stripe) &&
          paymentContext.paymentMethods.find((_) => _.name == 'card') &&
          stripeEnabled && (
            <Radio
              name="order-payment-method"
              isChecked={selectedPaymentMethodType === PaymentMethodType.Stripe && stripePaymentTypeSelected == 'card'}
              onChange={(isChecked) => {
                if (isChecked) {
                  setSelectedPaymentMethodType(PaymentMethodType.Stripe);
                  setStripePaymentTypeSelected('card');
                }
              }}
              label="Credit/Debit"
              type="white"
            />
          )}

        {allowedPaymentMethods.includes(PaymentMethodType.Stripe) &&
          paymentContext.paymentMethods.find((_) => _.name == 'card') &&
          stripeEnabled &&
          terminalOnline && (
            <>
              <Radio
                name="order-payment-method"
                isChecked={
                  selectedPaymentMethodType === PaymentMethodType.Stripe && stripePaymentTypeSelected == 'terminal'
                }
                onChange={(isChecked) => {
                  if (isChecked) {
                    setSelectedPaymentMethodType(PaymentMethodType.Stripe);
                    setStripePaymentTypeSelected('terminal');
                  }
                }}
                label="Swipe Credit/Debit"
                type="white"
              />
              {paymentContext.currency === 'CAD' && (
                <Radio
                  name="order-payment-method"
                  isChecked={
                    selectedPaymentMethodType === PaymentMethodType.Stripe &&
                    stripePaymentTypeSelected == 'terminal-bank'
                  }
                  onChange={(isChecked) => {
                    if (isChecked) {
                      setSelectedPaymentMethodType(PaymentMethodType.Stripe);
                      setStripePaymentTypeSelected('terminal-bank');
                    }
                  }}
                  label="Swipe Bank Card"
                  type="white"
                />
              )}
            </>
          )}

        {allowedPaymentMethods.includes(PaymentMethodType.Stripe) &&
          paymentContext.paymentMethods.find((_) => _.name == 'bank') &&
          stripeEnabled &&
          achEnabled && (
            <Radio
              name="order-payment-method"
              isChecked={
                selectedPaymentMethodType === PaymentMethodType.Stripe && stripePaymentTypeSelected == 'bankAccount'
              }
              onChange={(isChecked) => {
                if (isChecked) {
                  setSelectedPaymentMethodType(PaymentMethodType.Stripe);
                  setStripePaymentTypeSelected('bankAccount');
                }
              }}
              label="Bank Account"
              type="white"
            />
          )}

        {allowedPaymentMethods.includes(PaymentMethodType.Cash) &&
          paymentContext.paymentMethods.find((_) => _.name == 'cash') && (
            <Radio
              name="order-payment-method"
              isChecked={selectedPaymentMethodType === PaymentMethodType.Cash}
              onChange={(isChecked) => isChecked && setSelectedPaymentMethodType(PaymentMethodType.Cash)}
              label="Cash"
              type="white"
            />
          )}

        {allowedPaymentMethods.includes(PaymentMethodType.Check) &&
          paymentContext.paymentMethods.find((_) => _.name == 'check') && (
            <Radio
              name="order-payment-method"
              isChecked={selectedPaymentMethodType === PaymentMethodType.Check}
              onChange={(isChecked) => isChecked && setSelectedPaymentMethodType(PaymentMethodType.Check)}
              label="Check"
              type="white"
            />
          )}

        {allowedPaymentMethods.includes(PaymentMethodType.Other) &&
          paymentContext.paymentMethods.find((_) => _.name == 'other') && (
            <Radio
              name="order-payment-method"
              isChecked={selectedPaymentMethodType === PaymentMethodType.Other}
              onChange={(isChecked) => isChecked && setSelectedPaymentMethodType(PaymentMethodType.Other)}
              label="Other"
              type="white"
            />
          )}
        {allowedPaymentMethods.includes(PaymentMethodType.Statement) &&
          statementPaymentsEnabled &&
          paymentContext.paymentMethods.find((_) => _.name == 'overpayment') && (
            <Radio
              name="order-payment-method"
              isChecked={selectedPaymentMethodType === PaymentMethodType.Statement}
              onChange={(isChecked) => {
                isChecked && setSelectedPaymentMethodType(PaymentMethodType.Statement);
              }}
              label="Pay from remaining balance"
              type="white"
            />
          )}
        {allowedPaymentMethods.includes(PaymentMethodType.Credit) &&
          paymentContext.paymentMethods.find((_) => _.name == 'Credit') && (
            <Radio
              name="order-payment-method"
              isChecked={selectedPaymentMethodType === PaymentMethodType.Credit}
              onChange={(isChecked) => {
                isChecked && setSelectedPaymentMethodType(PaymentMethodType.Credit);
              }}
              label="Pay from Credit Memo"
              type="white"
            />
          )}
        {paymentContext.paymentMethods
          .filter((_) => _.type == PaymentMethodConfigType.Custom)
          .map((pm) => {
            return (
              <Radio
                key={pm.name}
                name="order-payment-method"
                isChecked={selectedPaymentMethodType === PaymentMethodType.Custom && customType == pm.name}
                onChange={(isChecked) => {
                  if (isChecked) {
                    console.log('setting custom payment method');
                    setSelectedPaymentMethodType(PaymentMethodType.Custom);
                    setCustomType(pm.name);
                  }
                }}
                label={pm.displayName}
                type="white"
              />
            );
          })}
      </div>

      <div>
        {selectedPaymentMethodType === PaymentMethodType.Stripe && stripePaymentTypeSelected == 'card' && (
          <CardPaymentMethodInput
            userId={user.id}
            paymentMethod={paymentMethod?.type === PaymentMethodType.Stripe ? paymentMethod : undefined}
            setPaymentMethod={setPaymentMethod}
            setCard={setCard}
          />
        )}
        {selectedPaymentMethodType === PaymentMethodType.Stripe && stripePaymentTypeSelected == 'terminal' && location && (
          <div className="m-[0.75rem]">
            <TerminalSelect
              locationId={locationId}
              setPaymentMethod={setPaymentMethod}
              close={() => setStripePaymentTypeSelected('card')}
              setCardId={(cardId: number) => setPaymentMethod({ type: PaymentMethodType.Stripe, cardId: cardId })}
              setCard={setCard}
              payments={payments}
              setPayments={setPayments}
              method={location.currency == 'USD' ? 'pay' : 'setup'}
              setMethod={() => {}}
              onComplete={(payments) => {
                location.currency == 'USD' && onComplete?.(payments);
              }}
              showFacilitator={facilitate}
              showSetupButton={location.currency !== 'USD'}
              user={user}
            />
          </div>
        )}
        {selectedPaymentMethodType === PaymentMethodType.Stripe &&
          stripePaymentTypeSelected == 'terminal-bank' &&
          location && (
            <div className="m-[0.75rem]">
              <TerminalSelect
                locationId={locationId}
                setPaymentMethod={(p) =>
                  setPaymentMethod({
                    ...p,
                    bankCard: true,
                  } as StripePaymentMethodRequest)
                }
                close={() => setStripePaymentTypeSelected('card')}
                setCardId={(cardId: number) => setPaymentMethod({ type: PaymentMethodType.Stripe, cardId: cardId })}
                setCard={setCard}
                payments={payments}
                setPayments={setPayments}
                method={'pay'}
                setMethod={() => {}}
                onComplete={(payments) => {
                  onComplete?.(payments);
                }}
                showFacilitator={facilitate}
                showSetupButton={false}
                user={user}
              />
            </div>
          )}
        {selectedPaymentMethodType === PaymentMethodType.Stripe && stripePaymentTypeSelected == 'bankAccount' && (
          <BankPaymentMethodInput
            userId={user.id}
            paymentMethod={paymentMethod?.type === PaymentMethodType.Stripe ? paymentMethod : undefined}
            setPaymentMethod={setPaymentMethod}
          />
        )}
        {selectedPaymentMethodType === PaymentMethodType.Cash && (
          <CashPaymentMethodInput
            paymentMethod={paymentMethod?.type === PaymentMethodType.Cash ? paymentMethod : undefined}
            setPaymentMethod={setPaymentMethod}
          />
        )}
        {selectedPaymentMethodType === PaymentMethodType.Check && (
          <CheckPaymentMethodInput
            paymentMethod={paymentMethod?.type === PaymentMethodType.Check ? paymentMethod : undefined}
            setPaymentMethod={setPaymentMethod}
          />
        )}
        {selectedPaymentMethodType === PaymentMethodType.Other && (
          <OtherPaymentMethodInput
            paymentMethod={paymentMethod?.type === PaymentMethodType.Other ? paymentMethod : undefined}
            setPaymentMethod={setPaymentMethod}
          />
        )}
        {selectedPaymentMethodType === PaymentMethodType.Statement && (
          <StatementPaymentMethodInput
            paymentMethod={paymentMethod?.type === PaymentMethodType.Statement ? paymentMethod : undefined}
            setPaymentMethod={setPaymentMethod}
            userId={user.id}
            setAmount={setAmount}
            balance={balance}
          />
        )}
        {selectedPaymentMethodType === PaymentMethodType.Credit && (
          <CreditPaymentMethodInput
            paymentMethod={paymentMethod?.type === PaymentMethodType.Credit ? paymentMethod : undefined}
            setPaymentMethod={setPaymentMethod}
            userId={user.id}
            setAmount={setAmount}
            balance={balance}
          />
        )}
        {selectedPaymentMethodType == PaymentMethodType.Custom &&
          paymentContext.paymentMethods
            .filter((_) => _.type == PaymentMethodConfigType.Custom && customType == _.name)
            .map((pm) => {
              return (
                <CustomPaymentMethodInput
                  name={pm.name}
                  setPaymentMethod={setPaymentMethod}
                  paymentMethod={paymentMethod?.type === PaymentMethodType.Custom ? paymentMethod : undefined}
                />
              );
            })}
      </div>
    </div>
  ) : (
    <Spinner />
  );
};

const StatementPaymentMethodInput = ({
  setPaymentMethod,
  paymentMethod,
  userId,
  setAmount,
  balance,
}: {
  paymentMethod?: StatementPaymentMethod;
  setPaymentMethod: (pm: PaymentMethodRequestV2) => void;
  userId: number;
  setAmount: (amount: number) => void;
  balance?: number;
}) => {
  const { getApplicablePaymentSummary } = useInvoiceApiV1();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [applicablePayments, setApplicablePayments] = useState<Payment[]>([]);

  async function loadApplicablePayments() {
    setIsLoading(true);
    try {
      const payments = await getApplicablePaymentSummary({ userId });
      setApplicablePayments(payments.unapplied);
    } catch (error) {
      toast('Failed to load applicable payments', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (!paymentMethod) {
      setPaymentMethod({ type: PaymentMethodType.Statement });
    }
    loadApplicablePayments();
  }, []);

  const paymentOptions = applicablePayments.map((p) => {
    return {
      label: `${p.methodDetail ?? p.method} - ${displayMoneyFormat(p.availableAmount)}`,
      value: p.id,
    };
  });

  return (
    <div className="flex m-[0.75rem]">
      {isLoading ? (
        <Spinner />
      ) : (
        <SelectForm
          options={paymentOptions}
          isSearchable={false}
          isDisabled={applicablePayments.length === 0}
          getOptionLabel={(p) => p.label}
          getOptionValue={(p) => p.value}
          onChange={(p) => {
            const paymentAmount = applicablePayments.find((_) => _.id == p?.value)?.availableAmount || 0;
            if (p) {
              setPaymentMethod({
                type: PaymentMethodType.Statement,
                paymentId: p.value,
              });
              if (!balance || balance > paymentAmount) {
                setAmount(paymentAmount);
              } else {
                setAmount(balance);
              }
            }
          }}
          placeholder={'Select payment'}
          components={{
            SingleValue: (props) => (
              <components.SingleValue {...props}>
                <Typography type="text-md" className="text-[var(--grey-800)]">
                  {props.children}
                </Typography>
              </components.SingleValue>
            ),
          }}
        />
      )}
    </div>
  );
};

const CreditPaymentMethodInput = ({
  setPaymentMethod,
  paymentMethod,
  userId,
  setAmount,
  balance,
}: {
  paymentMethod?: CreditPaymentMethod;
  setPaymentMethod: (pm: PaymentMethodRequestV2) => void;
  userId: number;
  setAmount: (amount: number) => void;
  balance?: number;
}) => {
  const { searchPayments } = useInvoiceApiV1();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [applicablePayments, setApplicablePayments] = useState<Payment[]>([]);

  async function loadCreditPayments() {
    setIsLoading(true);
    try {
      const payments = (
        await searchPayments({ userIds: [userId], methods: [PaymentMethodType.Credit], amountGreaterThan: 0 })
      ).items;
      setApplicablePayments(payments);
    } catch (error) {
      toast('Failed to load credit payments', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (!paymentMethod) {
      setPaymentMethod({ type: PaymentMethodType.Credit });
    }
    loadCreditPayments();
  }, []);

  const paymentOptions = applicablePayments.map((p) => {
    return {
      label: `${p.methodDetail ?? p.method}  ${displayMoneyFormat(p.availableAmount)}`,
      value: p.id,
    };
  });

  return (
    <div className="flex m-[0.75rem]">
      {isLoading ? (
        <Spinner />
      ) : (
        <SelectForm
          options={paymentOptions}
          isSearchable={false}
          isDisabled={applicablePayments.length === 0}
          getOptionLabel={(p) => p.label}
          getOptionValue={(p) => p.value}
          onChange={(p) => {
            const paymentAmount = (applicablePayments.find((_) => _.id == p?.value)?.availableAmount || 0) * -1;
            if (p) {
              setPaymentMethod({
                type: PaymentMethodType.Credit,
                paymentId: p.value,
              });
              if (!balance || balance > paymentAmount) {
                setAmount(paymentAmount);
              } else {
                setAmount(balance);
              }
            }
          }}
          placeholder={'Select payment'}
          components={{
            SingleValue: (props) => (
              <components.SingleValue {...props}>
                <Typography type="text-md" className="text-[var(--grey-800)]">
                  {props.children}
                </Typography>
              </components.SingleValue>
            ),
          }}
        />
      )}
    </div>
  );
};

const CashPaymentMethodInput = ({
  setPaymentMethod,
  paymentMethod,
}: {
  paymentMethod?: CashPaymentMethod;
  setPaymentMethod: (pm: CashPaymentMethod) => void;
}) => {
  useEffect(() => {
    if (!paymentMethod) {
      setPaymentMethod({ type: PaymentMethodType.Cash });
    }
  }, []);

  return (
    <div className="flex m-[0.75rem]">
      <Field
        type="text"
        value={paymentMethod?.comments}
        onChange={(comments) => {
          setPaymentMethod({
            type: PaymentMethodType.Cash,
            comments,
          });
        }}
        placeholder="Comments"
        label="Comments"
      />
    </div>
  );
};

const CheckPaymentMethodInput = ({
  setPaymentMethod,
  paymentMethod,
}: {
  paymentMethod?: CheckPaymentMethod;
  setPaymentMethod: (pm: CheckPaymentMethod) => void;
}) => {
  useEffect(() => {
    if (!paymentMethod) {
      setPaymentMethod({ type: PaymentMethodType.Check });
    }
  }, []);

  return (
    <div className="flex m-[0.75rem]">
      <Field
        type="number"
        value={paymentMethod?.checkNumber}
        onChange={(checkNumber) => {
          setPaymentMethod({
            type: PaymentMethodType.Check,
            checkNumber,
          });
        }}
        placeholder="Check Number"
        label="Check number"
        format={false}
      />
    </div>
  );
};

const OtherPaymentMethodInput = ({
  setPaymentMethod,
  paymentMethod,
}: {
  paymentMethod?: OtherPaymentMethod;
  setPaymentMethod: (pm: OtherPaymentMethod) => void;
}) => {
  useEffect(() => {
    if (!paymentMethod) {
      setPaymentMethod({ type: PaymentMethodType.Other });
    }
  }, []);
  return (
    <div className="flex m-[0.75rem]">
      <Field
        type="text"
        value={paymentMethod?.comments}
        onChange={(comments) => {
          setPaymentMethod({
            type: PaymentMethodType.Other,
            comments,
          });
        }}
        placeholder="Comments"
        label="Comments"
      />
    </div>
  );
};

const CustomPaymentMethodInput = ({
  name,
  setPaymentMethod,
  paymentMethod,
}: {
  name: string;
  paymentMethod?: CustomPaymentMethod;
  setPaymentMethod: (pm: CustomPaymentMethod) => void;
}) => {
  useEffect(() => {
    if (!paymentMethod) {
      setPaymentMethod({ type: PaymentMethodType.Custom, name });
    }
  }, []);
  return (
    <div className="flex m-[0.75rem]">
      <Field
        type="text"
        value={paymentMethod?.memo}
        onChange={(memo) => {
          setPaymentMethod({
            type: PaymentMethodType.Custom,
            name,
            memo,
          });
        }}
        placeholder="Memo"
        label="Memo"
      />
    </div>
  );
};

const BankPaymentMethodInput = ({
  userId,
  paymentMethod,
  setPaymentMethod,
}: {
  userId: number;
  paymentMethod?: StripePaymentMethodRequest;
  setPaymentMethod: (pm: StripePaymentMethodRequest) => void;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userBankAccounts, setUserBankAccounts] = useState<StripeBankAccount[]>([]);
  const [paymentBankAccount, setPaymentBankAccount] = useState<StripeBankAccount | null>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<ReactNode>();

  const { searchBankAccounts, deleteBankAccount } = usePaymentApiV2();
  const { sendEmailAsync } = useAPI();

  useEffect(() => {
    loadUserBankAccounts();
  }, [userId]);

  useEffect(() => {
    if (paymentBankAccount) {
      setPaymentMethod({ type: PaymentMethodType.Stripe, bankAccountId: paymentBankAccount.id });
    }
  }, [paymentBankAccount]);

  const loadUserBankAccounts = async () => {
    setIsLoading(true);
    try {
      const [activeBankAccounts, defaultBankAccounts] = await Promise.all([
        searchBankAccounts({ userIds: [userId] }),
        paymentMethod?.bankAccountId ? searchBankAccounts({ ids: [paymentMethod.bankAccountId] }) : Promise.resolve(),
      ]);
      const defaultBankAccount = defaultBankAccounts && defaultBankAccounts.find((b) => b);

      let bankAccounts = activeBankAccounts;
      if (defaultBankAccount && !activeBankAccounts.some((c) => c.id === defaultBankAccount.id)) {
        bankAccounts = [defaultBankAccount, ...activeBankAccounts];
      }
      setUserBankAccounts(bankAccounts);
      setPaymentBankAccount(defaultBankAccount || bankAccounts[0]);
    } catch (error) {
      toast('Failed to load user bank accounts', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnDelete = async (bankAccountId: string) => {
    try {
      await deleteBankAccount(bankAccountId, userId);
      toast('Account removed.', 'success');
      setDeleteConfirmModal(undefined);
      loadUserBankAccounts();
    } catch (error) {
      toast('Error removing card.', 'error');
    }
  };

  const sendBankLink = async () => {
    // send async email to customer__ach_account payment link
    await sendEmailAsync({ templateName: 'customer__bank_account_setup', userId });
    toast('Email sent.', 'success');
  };

  const BankAccountSelect = () => (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="card-list">
          {userBankAccounts.length === 0 && (
            <div className={'flex flex-column justify-items-center gap-2'}>
              <span>There are no bank accounts configured</span>
              <Button className={'button button-primary'} onClick={sendBankLink}>
                <Typography type="text-sm">Send Bank Account setup link</Typography>
              </Button>
            </div>
          )}
          {userBankAccounts.map((account, index) => {
            return (
              <div key={`card-user-body-${index}`} className="" tabIndex={1}>
                {
                  <Radio
                    isChecked={paymentBankAccount?.id === account.id && account.status === 'verified'}
                    name={`payment_account_${index}`}
                    onChange={(isChecked) => {
                      if (isChecked && account.status === 'verified') {
                        setPaymentBankAccount(account);
                      }
                    }}
                    label={
                      <div key={`account-user-title-${index}`} className="flex justify-between grow gap-2" tabIndex={0}>
                        <div className="flex items-center gap-2">
                          <span>
                            {'Account ending'} •••• {account.last4} - {'Status'} - {account.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {!account.deletedAt && (
                            <div
                              className="cursor-pointer text-[var(--link-200)]"
                              onClick={() => {
                                account.status === 'verified' && setPaymentBankAccount(account);
                              }}
                            >
                              Edit
                            </div>
                          )}
                          <div
                            className="cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              setDeleteConfirmModal(
                                <ConfirmModal
                                  prompt="Remove account?"
                                  additionalText="This cannot be undone"
                                  noLabel="Cancel"
                                  yesLabel="Remove"
                                  yesButtonYellow
                                  isOpen={true}
                                  headerIcon={
                                    <div className="flex justify-center">
                                      <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                                    </div>
                                  }
                                  onConfirm={() => handleOnDelete(account.id)}
                                  close={() => setDeleteConfirmModal(undefined)}
                                  zIndex={10000}
                                />,
                              );
                            }}
                          >
                            <Icon icon="Trash" />
                          </div>
                        </div>
                      </div>
                    }
                  ></Radio>
                }
              </div>
            );
          })}
        </div>
      )}

      {deleteConfirmModal}
    </>
  );

  return (
    <>
      <BankAccountSelect />
    </>
  );
};
